/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/


import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Car, Key, MapPin, Gauge, Menu, X, Clock, ChevronLeft, ChevronRight, CheckCircle2, Navigation as NavigationIcon, AlertTriangle, MessageCircle, Send, Phone } from 'lucide-react';
import FluidBackground from './components/FluidBackground';
import GradientText from './components/GlitchText';
import CustomCursor from './components/CustomCursor';
import Marquee from './components/Marquee';
import BookingModal from './components/BookingModal';
import ArtistCard from './components/ArtistCard';
import Navigation from './components/Navigation';
import LocationsSection from './components/LocationsSection';
import CookieConsent from './components/CookieConsent';
import ScrollToTop from './src/components/ScrollToTop';
import { Course } from './types';

// Lazy load heavy components
const AIChat = lazy(() => import('./components/AIChat'));
const FAQ = lazy(() => import('./components/FAQ'));
const Reviews = lazy(() => import('./components/Reviews'));
const Methodology = lazy(() => import('./components/Methodology'));
const About = lazy(() => import('./components/About'));
const Calculator = lazy(() => import('./components/Calculator'));
const ContactForm = lazy(() => import('./components/ContactForm'));
const Contact = lazy(() => import('./src/pages/Contact'));
const Blog = lazy(() => import('./src/pages/Blog'));
const BlogPostPage = lazy(() => import('./src/pages/BlogPostPage'));
const InstructorsPage = lazy(() => import('./src/pages/InstructorsPage'));
const AboutPage = lazy(() => import('./src/pages/AboutPage'));
const Fleet = lazy(() => import('./components/Fleet'));
const Branches = lazy(() => import('./components/Branches'));
const OrganizationInfo = lazy(() => import('./src/pages/legal/OrganizationInfo').then(m => ({ default: m.OrganizationInfo })));
const OfferAgreement = lazy(() => import('./src/pages/legal/OfferAgreement').then(m => ({ default: m.OfferAgreement })));
const PrivacyPolicy = lazy(() => import('./src/pages/legal/PrivacyPolicy').then(m => ({ default: m.PrivacyPolicy })));
const NotFoundPage = lazy(() => import('./src/pages/NotFoundPage'));

// Driving School Categories Data
const CATEGORIES: Course[] = [
  { 
    id: '1', 
    name: 'Категория А', 
    category: 'Мотоциклы', 
    duration: '1.5 Месяца', 
    image: '/images/categories/A-A1.webp',
    description: 'Курс управления мотоциклом. Отработка маневров на автодроме, экстренное торможение, баланс. Экипировка предоставляется бесплатно.'
  },
  { 
    id: '2', 
    name: 'Категория А1', 
    category: 'Мотоциклы', 
    duration: '1.5 Месяца', 
    image: '/images/categories/A-A1.webp',
    description: 'Курс управления легким мотоциклом. Отработка маневров на автодроме, экстренное торможение, баланс. Экипировка предоставляется бесплатно.'
  },
  { 
    id: '3', 
    name: 'Категория В', 
    category: 'Легковые', 
    duration: '3 Месяца', 
    image: '/images/categories/B-мкпп.webp',
    description: 'Обучение вождению легкового автомобиля. Полный курс теории и практики. Подготовка к экзамену в ГИБДД.'
  },
  { 
    id: '4', 
    name: 'Категория BE', 
    category: 'Грузовые', 
    duration: '3.5 Месяца', 
    image: '/images/categories/BE.webp',
    description: 'Профессиональное обучение на грузовых автомобилях. Устройство ТС, особенности габаритов, грузовая логистика. Переподготовка с B на C.'
  },
];

const BRANCHES = [
  { id: 1, area: 'Сосновский р-н (Долгодеревенское)', address: 'ул. Первого мая, д.50', phone: '+7 (351) 223-26-36' },
  { id: 2, area: 'Сосновский р-н (Полетаево)', address: 'ул. Полетаевская, д.48', phone: '+7 922 750 06 36' },
];

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Suspense fallback={<div className="min-h-screen bg-[#0f172a]" />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/branches" element={<Branches />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/about/fleet" element={<Fleet />} />
          <Route path="/fleet" element={<Fleet />} />
          <Route path="/about/instructors" element={<InstructorsPage />} />
          <Route path="/instructors" element={<InstructorsPage />} />
          <Route path="/legal/organization-info" element={<OrganizationInfo />} />
          <Route path="/legal/offer-agreement" element={<OfferAgreement />} />
          <Route path="/legal/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<{name: string; price: string; details?: string}>({
    name: '',
    price: '',
    details: ''
  });

  const openBookingModal = (plan: {name: string; price: string; details?: string}) => {
    setSelectedPlan(plan);
    setIsBookingModalOpen(true);
  };

  // Handle scroll to section from navigation state
  useEffect(() => {
    if (location.state?.scrollTo) {
      const sectionId = location.state.scrollTo;
      // Small delay to ensure page is loaded
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          const headerOffset = 100;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.scrollY - headerOffset;
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
      // Clear the state
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectedCourse) return;
      if (e.key === 'ArrowLeft') navigateCourse('prev');
      if (e.key === 'ArrowRight') navigateCourse('next');
      if (e.key === 'Escape') setSelectedCourse(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedCourse]);

  const navigateCourse = (direction: 'next' | 'prev') => {
    if (!selectedCourse) return;
    const currentIndex = CATEGORIES.findIndex(a => a.id === selectedCourse.id);
    let nextIndex;
    if (direction === 'next') {
      nextIndex = (currentIndex + 1) % CATEGORIES.length;
    } else {
      nextIndex = (currentIndex - 1 + CATEGORIES.length) % CATEGORIES.length;
    }
    setSelectedCourse(CATEGORIES[nextIndex]);
  };
  
  return (
    <div className="relative min-h-screen text-white selection:bg-[#f97316] selection:text-black cursor-auto md:cursor-none overflow-x-hidden">
      <CustomCursor />
      <FluidBackground />
      <Suspense fallback={<div />}>
        <AIChat />
      </Suspense>
      <CookieConsent />
      
      {/* Navigation */}
      <Navigation />

      {/* HERO SECTION */}
      <header className="relative h-[100svh] min-h-[600px] flex flex-col items-center justify-center overflow-hidden px-4">
        <motion.div 
          style={{ y, opacity }}
          className="z-10 text-center flex flex-col items-center w-full max-w-6xl pb-24 md:pb-20"
        >
           {/* Tagline */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="flex items-center gap-3 md:gap-6 text-xs md:text-sm font-mono text-[#f97316] tracking-[0.2em] md:tracking-[0.3em] uppercase mb-4 bg-black/40 px-6 py-2 rounded border border-white/10 backdrop-blur-sm"
          >
            <span>ГИБДД Аккредитация</span>
            <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-white rounded-full"/>
            <span>Старт групп еженедельно</span>
          </motion.div>

          {/* Main Title */}
          <h1 className="w-full flex flex-col items-center">
            <span className="sr-only">Автошкола Маэстро в Сосновском районе — обучение вождению в Долгодеревенском и Полетаево</span>
            <span className="text-4xl md:text-6xl font-heading font-semibold text-white/80 tracking-wider mb-4 text-center block" aria-hidden="true">
              АВТОШКОЛА
            </span>
            <span className="relative w-full flex justify-center items-center block">
              <GradientText 
                text="МАЭСТРО" 
                as="span" 
                className="text-[14vw] md:text-[13vw] leading-[0.9] font-black tracking-tighter text-center text-white" 
              />
              {/* Optimized Glow */}
              <motion.div 
                 className="absolute -z-20 w-[40vw] h-[40vw] bg-[#f97316]/20 blur-[60px] rounded-full pointer-events-none"
                 animate={{ scale: [0.9, 1.1, 0.9], opacity: [0.4, 0.6, 0.4] }}
                 transition={{ duration: 8, repeat: Infinity }}
              />
            </span>
          </h1>

          
          <motion.div
             initial={{ scaleX: 0 }}
             animate={{ scaleX: 1 }}
             transition={{ duration: 1.5, delay: 0.5, ease: "circOut" }}
             className="w-full max-w-md h-0.5 bg-gradient-to-r from-transparent via-[#f97316] to-transparent mt-8 md:mt-12 mb-6 md:mb-8"
          />

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 1 }}
            className="text-base md:text-xl font-light max-w-2xl mx-auto text-gray-300 leading-relaxed drop-shadow-lg px-4"
          >
            Научим водить уверенно и безопасно в любом районе города.
          </motion.p>
        </motion.div>

        {/* MARQUEE */}
        <Marquee />
      </header>

      {/* CATEGORIES SECTION */}
      <section id="courses" className="relative z-10 py-12 bg-[#0f172a]">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row justify-between items-end mb-12 md:mb-16 px-4 border-l-4 border-[#f97316] pl-6">
             <h2 className="text-4xl md:text-7xl font-heading font-bold uppercase leading-[0.9] break-words w-full md:w-auto text-white">
              <span className="text-[#94a3b8]">Наши</span> <br/> 
              Категории
            </h2>
            <p className="text-gray-400 max-w-md text-right mt-4 md:mt-0 font-mono text-sm uppercase">
              Обучение на все виды транспорта от мотоцикла до грузовика
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-white/10 bg-[#1e293b]/50 backdrop-blur-sm">
            {CATEGORIES.map((course) => (
              <ArtistCard key={course.id} artist={course} onClick={() => setSelectedCourse(course)} />
            ))}
          </div>
        </div>
      </section>

      {/* METHODOLOGY SECTION */}
      <Suspense fallback={<div className="py-32" />}>
        <Methodology />
      </Suspense>

      {/* ABOUT SECTION */}
      <Suspense fallback={<div className="py-32" />}>
        <About />
      </Suspense>

      {/* AUTODROM & ADVANTAGES */}
      <section id="autodrom" className="relative z-10 py-12 bg-[#0f172a] border-t border-white/10 overflow-hidden">

        <div className="max-w-7xl mx-auto px-4 md:px-6 relative">
          <div className="flex flex-col md:flex-row justify-between items-end mb-12 md:mb-16 px-4 border-l-4 border-[#f97316] pl-6">
            <h2 className="text-4xl md:text-7xl font-heading font-bold uppercase leading-[0.9] break-words w-full md:w-auto text-white">
              <span className="text-[#94a3b8]">Твой путь</span> <br/> 
              К Правам
            </h2>
            <p className="text-gray-400 max-w-md text-right mt-4 md:mt-0 font-mono text-sm uppercase">
              Собственный автодром и современный автопарк
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 md:gap-16 items-center">
            <div className="lg:col-span-5 order-2 lg:order-1">
              <p className="text-lg md:text-xl text-gray-300 mb-8 md:mb-12 font-light leading-relaxed">
                Автошкола с собственным асфальтированным автодромом. Никаких очередей на упражнениях.
              </p>
              
              <div className="space-y-6 md:space-y-8">
                {[
                  { icon: NavigationIcon, title: 'Свой Автодром', desc: 'Освещение, разметка, эстакада. Работает до 23:00.' },
                  { icon: Car, title: 'Парк Автомобилей', desc: 'Volkswagen Polo, Hyundai Solaris, Skoda Rapid не старше 3 лет.' },
                  { icon: AlertTriangle, title: 'Без скрытых платежей', desc: 'Бензин и страховка уже включены в стоимость.' },
                ].map((feature, i) => (
                  <div
                    key={i} 
                    className="flex items-start gap-6 group"
                  >
                    <div className="p-4 rounded-xl bg-white/5 border border-white/10 group-hover:border-[#f97316]/50 transition-colors">
                      <feature.icon className="w-6 h-6 text-[#f97316]" />
                    </div>
                    <div>
                      <h4 className="text-lg md:text-xl font-bold mb-1 md:mb-2 font-heading">{feature.title}</h4>
                      <p className="text-sm text-gray-400">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-7 relative h-[400px] md:h-[600px] w-full order-1 lg:order-2">
              <div className="absolute inset-0 bg-gradient-to-br from-[#1e3a8a] to-[#f97316] rounded-sm -rotate-3 opacity-20" />
              <div className="relative h-full w-full rounded-sm overflow-hidden border border-white/10 group shadow-2xl">
                <img 
                  src="/images/autodrom/auto-drom.webp" 
                  alt="Автодром Маэстро" 
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover grayscale group-hover:grayscale-0 transition-all duration-[1s]" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a] via-transparent to-transparent opacity-90" />
                
                <div className="absolute bottom-6 left-6 md:bottom-10 md:left-10">
                  <div className="text-5xl md:text-7xl font-heading font-bold text-white opacity-80">
                    95%
                  </div>
                  <div className="text-sm md:text-base font-bold tracking-widest uppercase mt-2 text-[#f97316] bg-black/50 inline-block px-3 py-1 backdrop-blur-md">
                    Сдают экзамен с первого раза
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

       {/* LOCATIONS SECTION */}
       <LocationsSection />

      {/* FAQ SECTION */}
      <Suspense fallback={<div className="py-32" />}>
        <FAQ />
      </Suspense>

      {/* REVIEWS SECTION */}
      <Suspense fallback={<div className="py-32" />}>
        <Reviews />
      </Suspense>


      {/* PRICING */}
      <section id="pricing" className="relative z-10 py-12 px-4 md:px-6 bg-[#0f172a]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-end mb-12 md:mb-20 px-4 border-l-4 border-[#f97316] pl-6">
            <h2 className="text-4xl md:text-7xl font-heading font-bold uppercase leading-[0.9] break-words w-full md:w-auto text-white">
              <span className="text-[#94a3b8]">Наши</span> <br/> 
              Цены
            </h2>
            <p className="text-gray-400 max-w-md text-right mt-4 md:mt-0 font-mono text-sm uppercase">
              Рассрочка 0% на весь период обучения
            </p>
          </div>

          <div className="mb-16">
            <Suspense fallback={<div className="h-96" />}>
              <Calculator onBooking={openBookingModal} />
            </Suspense>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {[
              { 
                name: 'Студент', 
                price: '46 000₽', 
                desc: 'При предъявлении студенческого билета',
                features: ['Теория в классе в дневное время', '56 часов вождения (МКПП/АКПП) в дневное время', 'Сопровождение в ГИБДД'] 
              },
              { 
                name: 'Стандарт', 
                price: '50 000₽', 
                desc: 'Самый популярный',
                features: ['Полный курс теории', '56 часов вождения (МКПП/АКПП)', 'Подача авто на экзамен', 'Сопровождение в ГИБДД'] 
              },
              { 
                name: 'Премиум', 
                price: '55 000₽', 
                desc: 'Все включено',
                features: ['Полный курс теории', '56 часов вождения (МКПП/АКПП)', 'Подача авто на экзамен', 'Сопровождение в ГИБДД', 'Выбор инструктора', 'Индивидуальный график', 'Отработка экзаменационного маршрута'] 
              },
            ].map((tier, i) => {
              const isPopular = i === 1;

              return (
                <div
                  key={i}
                  className={`relative p-8 md:p-10 border ${isPopular ? 'border-[#f97316] bg-[#f97316]/5' : 'border-white/10 bg-[#1e293b]/50'} backdrop-blur-md flex flex-col`}
                >
                  {isPopular && (
                    <div className="absolute top-0 right-0 bg-[#f97316] text-black text-xs font-bold px-3 py-1 uppercase">
                      Хит
                    </div>
                  )}
                  
                  <h3 className="text-4xl md:text-5xl font-heading font-bold mb-3 text-white">{tier.name}</h3>
                  <p className="text-gray-400 text-xl md:text-2xl mb-6">{tier.desc}</p>
                  <div className={`text-5xl md:text-6xl font-bold mb-8 tracking-tighter text-white`}>
                    {tier.price}
                  </div>
                  
                  <div className="w-full h-px bg-white/10 mb-8" />
                  
                  <ul className="space-y-4 text-sm text-gray-300 flex-1">
                    {tier.features.map((feat, idx) => (
                       <li key={idx} className="flex items-start gap-3">
                         <CheckCircle2 className="w-5 h-5 text-[#f97316] shrink-0" /> 
                         {feat}
                       </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => openBookingModal({
                      name: tier.name,
                      price: tier.price,
                      details: tier.desc
                    })}
                    className="w-full mt-8 bg-[#f97316] text-black font-bold uppercase tracking-widest py-3 rounded hover:bg-white transition-colors"
                  >
                    Записаться
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <footer className="relative z-10 border-t border-white/10 py-12 md:py-16 bg-[#020617]">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-start md:items-end gap-8">
          <div>
             <div className="font-heading text-2xl md:text-3xl font-bold tracking-tighter mb-4 text-white flex items-center gap-2">
                <img src="/new_logo.png" alt="Маэстро" className="w-10 h-10 object-contain" />
                МАЭСТРО
             </div>
             <div className="flex flex-col gap-2 text-sm text-gray-400 font-light">
               <span className="font-semibold text-white mb-1">Наши телефоны:</span>
               <a href="tel:+73512232636" className="hover:text-[#f97316] transition-colors">+7 (351) 223-26-36</a>
               <a href="tel:+73517500636" className="hover:text-[#f97316] transition-colors">+7 (351) 750-06-36</a>
               <a href="tel:+79227500636" className="hover:text-[#f97316] transition-colors">+7 922 750 06 36</a>
               <a href="tel:+79191232636" className="hover:text-[#f97316] transition-colors">+7 919 123 26 36</a>
               <span className="mt-2">maestro-avtoshkola@mail.ru</span>
               <div className="flex gap-4 mt-4">
                 <a href="https://vk.com/maestroplus74" target="_blank" rel="noopener noreferrer" className="text-[#f97316] hover:text-white transition-colors" data-hover="true">
                   <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M15.07 2H8.93C3.33 2 2 3.33 2 8.93v6.14C2 20.67 3.33 22 8.93 22h6.14c5.6 0 6.93-1.33 6.93-6.93V8.93C22 3.33 20.67 2 15.07 2zm3.45 14.98h-1.4c-.56 0-.73-.45-1.73-1.45-1.01-1-1.49-1.13-1.75-1.13-.36 0-.46.1-.46.58v1.32c0 .36-.11.57-1.06.57-1.56 0-3.29-.94-4.51-2.7-1.83-2.53-2.33-4.44-2.33-4.83 0-.26.1-.5.58-.5h1.4c.43 0 .6.2.76.66.85 2.42 2.28 4.54 2.87 4.54.22 0 .32-.1.32-.66v-2.56c-.07-1.32-.77-1.43-.77-1.9 0-.21.17-.42.45-.42h2.2c.36 0 .49.2.49.63v3.47c0 .36.16.49.26.49.22 0 .4-.13.82-.55 1.29-1.45 2.22-3.69 2.22-3.69.12-.26.32-.5.75-.5h1.4c.42 0 .51.21.42.63-.15.99-1.68 3.59-1.68 3.59-.18.3-.25.44 0 .77.18.25.78.76 1.18 1.22.73.82 1.3 1.51 1.45 1.99.15.47-.08.71-.55.71z"/></svg>
                 </a>
               </div>
               <span className="mt-4 text-xs opacity-50">© 2024 Автошкола Маэстро. Все права защищены.</span>
             </div>
          </div>
          
          <div className="flex gap-6 md:gap-8 flex-wrap">
            <a href="/legal/organization-info.html" className="text-gray-400 hover:text-[#f97316] font-bold uppercase text-xs tracking-widest transition-colors">Сведения об организации</a>
            <a href="/legal/offer-agreement.html" className="text-gray-400 hover:text-[#f97316] font-bold uppercase text-xs tracking-widest transition-colors">Договор оферты</a>
            <a href="/legal/privacy-policy.html" className="text-gray-400 hover:text-[#f97316] font-bold uppercase text-xs tracking-widest transition-colors">Политика конфиденциальности</a>
          </div>
        </div>
      </footer>

      {/* Booking Modal */}
      <BookingModal
        isOpen={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
        selectedPlan={selectedPlan}
      />

      {/* Course Detail Modal */}
      <AnimatePresence>
        {selectedCourse && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedCourse(null)}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm cursor-auto"
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-5xl bg-[#1e293b] border border-white/10 overflow-hidden flex flex-col md:flex-row shadow-2xl shadow-black"
            >
              {/* Close Button */}
              <button
                onClick={() => setSelectedCourse(null)}
                className="absolute top-4 right-4 z-20 p-2 rounded-full bg-black/50 text-white hover:bg-[#f97316] hover:text-white transition-colors"
                data-hover="true"
              >
                <X className="w-6 h-6" />
              </button>

              {/* Navigation Buttons */}
              <button
                onClick={(e) => { e.stopPropagation(); navigateCourse('prev'); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 md:left-8 z-20 p-3 rounded-full bg-transparent text-white/60 hover:bg-[#f97316] hover:text-white transition-all border border-white/30"
                data-hover="true"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>

              <button
                onClick={(e) => { e.stopPropagation(); navigateCourse('next'); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 md:right-8 z-20 p-3 rounded-full bg-transparent text-white/60 hover:bg-[#f97316] hover:text-white transition-all border border-white/30"
                data-hover="true"
              >
                <ChevronRight className="w-6 h-6" />
              </button>

              {/* Image Side */}
              <div className="w-full md:w-1/2 aspect-square relative overflow-hidden bg-[#0f172a] flex items-center justify-center">
                <AnimatePresence mode="wait">
                  <motion.img 
                    key={selectedCourse.id}
                    src={selectedCourse.image} 
                    alt={selectedCourse.name} 
                    loading="lazy"
                    decoding="async"
                    initial={{ opacity: 0, scale: 1.1 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                    className="w-full h-full object-contain opacity-90"
                  />
                </AnimatePresence>
              </div>

              {/* Content Side */}
              <div className="w-full md:w-1/2 p-8 pb-24 md:p-12 flex flex-col justify-center relative bg-[#1e293b]">
                <motion.div
                  key={selectedCourse.id}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.1 }}
                >
                  <div className="flex items-center gap-3 text-[#f97316] mb-4">
                     <Clock className="w-4 h-4" />
                     <span className="font-mono text-sm tracking-widest uppercase">{selectedCourse.duration}</span>
                     <span className="w-1 h-1 bg-white/20 rounded-full" />
                     <span className="font-mono text-sm tracking-widest uppercase text-gray-400">{selectedCourse.category}</span>
                  </div>
                  
                  <h3 className="text-3xl md:text-5xl font-heading font-bold uppercase leading-none mb-6 text-white">
                    {selectedCourse.name}
                  </h3>
                  
                  <div className="h-0.5 w-20 bg-[#f97316] mb-6" />
                  
                  <p className="text-gray-300 leading-relaxed text-base md:text-lg font-light mb-8">
                    {selectedCourse.description}
                  </p>

                  <button 
                    onClick={() => {
                      setSelectedCourse(null);
                      // Scroll to pricing section
                      setTimeout(() => {
                        const element = document.getElementById('pricing');
                        if (element) {
                          const headerOffset = 100;
                          const elementPosition = element.getBoundingClientRect().top;
                          const offsetPosition = elementPosition + window.scrollY - headerOffset;
                          window.scrollTo({
                            top: offsetPosition,
                            behavior: 'smooth'
                          });
                        }
                      }, 300);
                    }}
                    className="bg-[#f97316] text-black font-bold uppercase tracking-widest py-3 px-6 hover:bg-white transition-colors self-start rounded-lg"
                  >
                    Записаться на курс
                  </button>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default App;